import { Box, Typography } from "@outschool/backpack";
import { RefundPolicies } from "@outschool/business-rules";
import { useLocale, useTranslation } from "@outschool/localization";
import { refundPolicyCreditsUrl, refundPolicyUrl } from "@outschool/routes";
import { gql } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { ExternalLink } from "@outschool/ui-components-shared";
import {
  useActivityString,
  useEnrollmentServiceFee,
} from "@outschool/ui-components-website";
import React from "react";

import { useOrderContext } from "../OrderContext";

export default function RefundPolicy({
  priceCents,
  isSelfPaced = false,
  isClub = false,
}: {
  priceCents: number | null;
  isSelfPaced?: boolean;
  isClub?: boolean;
}) {
  const locale = useLocale();
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\RefundPolicy",
  );

  const { currentUser } = useSession();
  const { isUsingPurchaseOrder, pricingBreakdown } = useOrderContext();
  const { serviceFeePercentage } = useEnrollmentServiceFee({
    isUsingPurchaseOrder,
    isUsingPricingOffer: !!pricingBreakdown?.pricingOfferCents,
  });

  const { refundPolicyString } = useActivityString();
  const {
    refundPolicyDescription,
    refundPolicyCreditDescription,
    refundPolicyCreditDescriptionSelfPaced,
    refundPolicyCreditDescriptionClub,
  } = refundPolicyString(RefundPolicies.RefundPolicy.Limited);

  if (priceCents === null || priceCents <= 0) {
    return null;
  }

  const serviceFeeRefundPolicyDescription = t(
    "Cancel within 24 hours of purchase to receive a refund. If the class meets within \
    24 hours, cancel before it begins to receive a refund.",
  );

  const serviceFeeRefundPolicyDescriptionSelfPaced = t(
    "Cancel within 24 hours of purchase to receive a refund. If class access begins within \
    24 hours, cancel before access starts to receive a refund.",
  );

  const serviceFeeRefundPolicyDescriptionClub = t(
    "Cancel within 24 hours of purchase to receive a refund.",
  );

  let refundPolicyText;
  // 'canPayWithSubscriptionCredits', 'service fee', 'no service fee' are mutually exclusive and collectively exhaustive.
  if (currentUser?.canPayWithSubscriptionCredits) {
    refundPolicyText = isSelfPaced
      ? refundPolicyCreditDescriptionSelfPaced
      : isClub
      ? refundPolicyCreditDescriptionClub
      : refundPolicyCreditDescription;
  } else {
    if (serviceFeePercentage) {
      refundPolicyText = isSelfPaced
        ? serviceFeeRefundPolicyDescriptionSelfPaced
        : isClub
        ? serviceFeeRefundPolicyDescriptionClub
        : serviceFeeRefundPolicyDescription;
    } else {
      refundPolicyText = refundPolicyDescription;
    }
  }

  return (
    <Box
      sx={{
        paddingLeft: "1em",
        paddingRight: "1em",
        paddingTop: "0.5em",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: "grey.900",
        }}
      >
        {refundPolicyText}{" "}
        <ExternalLink
          url={
            currentUser?.canPayWithSubscriptionCredits
              ? refundPolicyCreditsUrl(locale)
              : refundPolicyUrl(locale)
          }
        >
          {t("Learn More")}
        </ExternalLink>
      </Typography>
    </Box>
  );
}

RefundPolicy.fragments = {
  activity: gql`
    fragment RefundPolicy_activity on Activity {
      isClub
      is_ongoing_weekly
      allows_recurring_payment
      hasTeacherSchedule
      size_max
      duration_weeks
      weekly_meetings
      refundPolicy {
        name
        description
        type
      }
    }
  `,
};
